import { CheckCircleFilled, CloudUploadOutlined, ExclamationCircleFilled } from '@ant-design/icons'
import { Alert, Button, Card, Descriptions, Tag, Typography, message } from 'antd'
import { useState } from 'react'
import { publishSite } from '../../api'
import type { SaasApiError } from '../../api'
import type { PublishTriggerResult } from '../../types'

const { Title, Paragraph } = Typography

const STATUS_LABELS: Record<string, string> = {
  completed: '成功',
  completed_with_warnings: '成功（有警告）',
  failed: '失败',
}
const statusLabel = (status: string) => STATUS_LABELS[status] ?? status

/**
 * Publish tab — the "Local Contract" for Publish (spec §14): Draft ->
 * Snapshot -> Rendering, via the real, unmodified `PublishOrchestrator`
 * (`app/saas/services/website_publish_service.py`) against a local
 * filesystem target. No Cloudflare/production publish in scope this
 * phase (spec §14/§15/§27) — clicking Publish here only ever creates a
 * *Local* Published Snapshot, which the Preview tab's "Published
 * Snapshot" mode then reads back verbatim.
 */
export default function PublishTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [publishing, setPublishing] = useState(false)
  const [lastResult, setLastResult] = useState<PublishTriggerResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const onPublish = async () => {
    setPublishing(true)
    setError(null)
    try {
      const { data } = await publishSite(workspaceId, siteId)
      setLastResult(data)
      if (data.errors.length === 0) {
        message.success('已发布 — 新的快照已在本地预览中生效')
      } else {
        message.warning('发布完成但存在错误，详情见下方')
      }
    } catch (err) {
      setError((err as SaasApiError).message)
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div style={{ marginTop: 16, maxWidth: 640 }}>
      <Title level={5}>发布</Title>
      <Paragraph type="secondary">
        发布仅会将状态为<Tag>已发布</Tag>的条目渲染进一份全新的、不可变更的发布快照（本地版本，本阶段不涉及生产/CDN
        分发）。草稿内容在再次发布之前不会出现在此处。
      </Paragraph>
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Button type="primary" icon={<CloudUploadOutlined />} loading={publishing} onClick={onPublish} size="large">
        发布
      </Button>

      {lastResult && (
        <Card style={{ marginTop: 24 }} title="最近一次发布结果">
          <Descriptions column={1} size="small">
            <Descriptions.Item label="状态">
              {lastResult.status.startsWith('completed') ? (
                <Tag color="green" icon={<CheckCircleFilled />}>
                  {statusLabel(lastResult.status)}
                </Tag>
              ) : (
                <Tag color="red" icon={<ExclamationCircleFilled />}>
                  {statusLabel(lastResult.status)}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="版本号">{lastResult.revision_id}</Descriptions.Item>
            <Descriptions.Item label="包校验和">{lastResult.package_checksum ?? '—'}</Descriptions.Item>
            {lastResult.warnings.length > 0 && (
              <Descriptions.Item label="警告">{lastResult.warnings.join(', ')}</Descriptions.Item>
            )}
            {lastResult.errors.length > 0 && (
              <Descriptions.Item label="错误">{lastResult.errors.join(', ')}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      <Paragraph type="secondary" style={{ marginTop: 24 }}>
        切换到「网站预览」并选择「已发布快照」即可查看刚刚发布的内容。
      </Paragraph>
    </div>
  )
}
